import ProductCard from '@/components/productCard'
import Loader from '@/components/ui/loader'
import { useContentContext } from '@/context/ContentContext'
import { useLanguage } from '@/context/LanguageContext'
import { useProductApi } from '@/services/m-commerce-api/product'
import { Category } from '@/services/m-commerce-api/product/categories'
import {
  ProductMin,
  ShopParams
} from '@/services/m-commerce-api/product/product'
import { Subcategory } from '@/services/m-commerce-api/product/subcategories'
import useIntl from '@/utils/intl'
import { isEmptyString } from '@/utils/validations'
import {
  CloseOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
  PanoramaSharp
  , ChevronLeft, ChevronRight
} from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputBase,
  ListItem,
  ListItemText,
  RadioGroup,
  Skeleton,
  Slider,
  SwipeableDrawer
} from '@mui/material'
import Image from 'next/image'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import React, {
  FunctionComponent,
  useEffect,
  useReducer,
  useRef,
  useState
} from 'react'
import { trackShopEvent } from '@/utils/shopFlowTracking'
import DarkButton from '../ui/Buttons/SecondaryButton'
import { resolveImageUrl } from '@/utils/constants'
import DarkIconButton from '../ui/Buttons/SecondaryIconButton'
import classNames from 'classnames'
import { getHomePageContents } from '@/services/cms-api/home-page-content'
import { HomePageContent } from '@/services/cms-api/home-page-content/types'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Autoplay } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import { Swiper as SwiperType } from 'swiper'
import { useTheme } from '@/context/ThemeContext'
// const PRICE_RANGES = [
//   {
//     text: '0₮ - 1,000,000₮',
//     lte: 1000000
//   },
//   {
//     text: '1,000,000₮ - 2,000,000₮',
//     gte: 1000000,
//     lte: 2000000
//   },
//   {
//     text: '2,000,000₮ - 3,500,000₮',
//     gte: 2000000,
//     lte: 3500000
//   },
//   {
//     text: '3,500,000₮ - 5,000,000₮',
//     gte: 3500000,
//     lte: 5000000
//   },
//   {
//     text: '5,000,000₮+',
//     gte: 5000000,
//     lte: undefined
//   }
// ]

interface State {
  loading?: boolean
  selected?: string
  products?: ProductMin[]
  drawerOpen?: boolean
  selectedCategories?: Category[]
  selectedSubcategories?: Subcategory[]
  priceRangeIdx?: number
  catId?: string
  subCatId?: string
  priceLte?: string
  priceGte?: string
  search?: string
  page?: number
  size?: number
  hasGift?: boolean
  hasSale?: boolean
  openType?: boolean
  openBrand?: boolean
  openPrice?: boolean
  showCategory?: boolean
  priceAsc?: string
  priceDesc?: string
  selectedIndex?: number
}

interface RouteParam {
  action?: 'DEL' | 'SET'
  routes?: Record<string, string | undefined> | string[]
  push?: boolean
}

const Shop: FunctionComponent = () => {
  const { locale } = useLanguage()
  const router = useRouter()
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const queryParams = new URLSearchParams(Array.from(searchParams.entries()))
  const [homeContent, setHomeContent] = useState<HomePageContent | null>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [totalImages, setTotalImages] = useState(0)
  const swiperRef = React.useRef<SwiperType | null>(null)
  const shopViewTracked = useRef(false)
  const lastPromoIndex = useRef<number | null>(null)
  const [showSortOptions, setShowSortOptions] = useState(false)
  const { theme, setTheme } = useTheme()
  const { getProducts } = useProductApi()
  const {
    state: { categoryTree, subcategories }
  } = useContentContext()
  const { translate } = useIntl()
  const [state, setState] = useReducer(
    (state: State, newState: State) => ({ ...state, ...newState }),
    {
      loading: true,
      selected: translate('all'),
      products: [],
      selectedSubcategories: [],
      selectedCategories: [],
      drawerOpen: false,
      catId: '',
      subCatId: '',
      priceLte: '',
      priceGte: '',
      // priceRangeIdx: -1,
      search: '',
      hasGift: false,
      hasSale: false,
      openType: true,
      openBrand: true,
      openPrice: true,
      priceAsc: 'false',
      priceDesc: 'false',
      showCategory: false
    }
  )
  useEffect(() => {
    getParams().catch((e) => console.error(e))
  }, [searchParams, categoryTree])

  useEffect(() => {
    if (shopViewTracked.current) {
      return
    }
    shopViewTracked.current = true
    trackShopEvent('shop_view')
  }, [])

  const getBrandName = (id?: string): string => {
    if (id == null || id === '') {
      return 'unknown'
    }
    const fromSelected = state.selectedSubcategories?.find(
      (item) => item.id.toString() === id
    )?.name
    if (fromSelected != null && fromSelected !== '') {
      return fromSelected
    }
    const fromAll = subcategories?.find((item) => item.id.toString() === id)?.name
    if (fromAll != null && fromAll !== '') {
      return fromAll
    }
    for (const category of categoryTree ?? []) {
      const found = category.productSubCategoryDto?.find(
        (item) => item.id.toString() === id
      )
      if (found?.name != null && found.name !== '') {
        return found.name
      }
    }
    return 'unknown'
  }

  const trackPromoImpression = (index: number): void => {
    const banners = homeContent?.onlineshopBanners
    if (banners == null || banners[index] == null) {
      return
    }
    if (lastPromoIndex.current === index) {
      return
    }
    lastPromoIndex.current = index
    const banner = banners[index]
    const promoTitle = locale === 'en' ? banner.titleEn : banner.titleMn
    trackShopEvent('view_promotion', {
      promo_id: banner.id,
      promo_title: promoTitle,
      promotion_id: banner.id,
      promotion_name: promoTitle
    })
  }

  const trackPromoClick = (index: number): void => {
    const banners = homeContent?.onlineshopBanners
    if (banners == null || banners[index] == null) {
      return
    }
    const banner = banners[index]
    const promoTitle = locale === 'en' ? banner.titleEn : banner.titleMn
    trackShopEvent('select_promotion', {
      promo_id: banner.id,
      promo_title: promoTitle,
      promotion_id: banner.id,
      promotion_name: promoTitle
    })
  }

  const getParams = async (): Promise<void> => {
    if (categoryTree === undefined) {
      return
    }
    const catId = searchParams.get('catId')
    const subCatId = searchParams.get('subCatId')
    const priceGte = searchParams.get('priceGte')
    const priceLte = searchParams.get('priceLte')
    const priceDesc = searchParams.get('priceDesc')
    const priceAsc = searchParams.get('priceAsc')
    const q = searchParams.get('q')
    const hasGift = searchParams.get('gift')
    const hasSale = searchParams.get('sale')

    if (!isEmptyString(catId)) {
      const category = categoryTree?.find(
        (e) => e.id.toString() === String(catId)
      )
      if (category != null) {
        setState({
          selected: locale === 'en' ? category.nameEn : category.nameMn,
          selectedSubcategories: category?.productSubCategoryDto
        })
      } else {
        setState({
          selected: translate('all'),
          selectedSubcategories: subcategories
        })
      }
    } else {
      setState({
        selected: translate('all'),
        selectedSubcategories: []
      })
    }
    const query: ShopParams = {
      categoryId: !isEmptyString(catId) ? String(catId) : undefined,
      subCategoryId: !isEmptyString(subCatId) ? String(subCatId) : undefined,
      minPrice: !isEmptyString(priceGte) ? String(priceGte) : undefined,
      maxPrice: !isEmptyString(priceLte) ? String(priceLte) : undefined,
      priceAsc: !isEmptyString(priceAsc) ? String(priceAsc) : undefined,
      priceDesc: !isEmptyString(priceDesc) ? String(priceDesc) : undefined,
      nameEn: !isEmptyString(q) ? String(q) : undefined,
      nameMn: !isEmptyString(q) ? String(q) : undefined,
      giftMn: !isEmptyString(hasGift) ? 'бэлэг' : undefined,
      sale: !isEmptyString(hasSale) ? String(hasSale) : undefined
    }
    setState({
      catId: !isEmptyString(catId) ? String(catId) : '',
      subCatId: !isEmptyString(subCatId) ? String(subCatId) : '',
      priceGte: !isEmptyString(priceGte) ? String(priceGte) : '',
      priceLte: !isEmptyString(priceLte) ? String(priceLte) : '',
      priceAsc: !isEmptyString(priceAsc) ? String(priceAsc) : '',
      priceDesc: !isEmptyString(priceDesc) ? String(priceDesc) : '',
      search: !isEmptyString(q) ? String(q) : '',
      hasGift: !isEmptyString(hasGift),
      hasSale: !isEmptyString(hasSale)
    })
    await fetchData(query)
  }
  useEffect(() => {
    const fetchHomePageContent = async () => {
      try {
        const data = await getHomePageContents()
        if (data) {
          setHomeContent(data)
        }
      } catch (error) {
        console.error('Error fetching home page content:', error)
      }
    }

    fetchHomePageContent()
  }, [])
  const handleCategorySelect = async (
    value: string,
    id: number
  ): Promise<void> => {
    setState({
      selected: value,
      showCategory: false,
      subCatId: '',
      priceLte: '',
      priceGte: '',
      priceRangeIdx: undefined,
      search: ''
    })
    handleClearFilters()

    if (value === translate('all')) {
      setState({ catId: '' })
      handleRoute({ action: 'DEL', routes: ['catId'] })
    } else {
      if (categoryTree !== undefined) {
        if (id !== undefined) {
          setState({ catId: id.toString() })
          handleRoute({ action: 'SET', routes: { catId: id.toString() } })
        }
      }
    }
    setState({ subCatId: '', priceGte: '', priceLte: '' })
  }
  const handleBrandSelect = async (id?: string): Promise<void> => {
    if (id === undefined) {
      return
    }
    if (id === state.subCatId) {
      setState({ subCatId: undefined })
      handleRoute({ action: 'DEL', routes: ['subCatId'] })
    } else {
      const brandName = getBrandName(id)
      setState({ subCatId: id })
      handleRoute({ action: 'SET', routes: { subCatId: id.toString() } })
      trackShopEvent('select_brand', { brand_name: brandName })
      trackShopEvent('shop_filter', {
        filter_type: 'brand',
        filter_value: brandName
      })
    }
  }

  // const handlePriceSelect = async (idx?: number): Promise<void> => {
  //   if (idx === undefined) {
  //     return
  //   }
  //   const lte = PRICE_RANGES[idx].lte?.toString()
  //   const gte = PRICE_RANGES[idx].gte?.toString()

  //   if (idx === state.priceRangeIdx) {
  //     setState({ priceRangeIdx: undefined, priceGte: undefined, priceLte: undefined })
  //     handleRoute({
  //       action: 'DEL',
  //       routes: ['priceGte', 'priceLte']
  //     })
  //   } else {
  //     setState({ priceRangeIdx: idx, priceGte: gte, priceLte: lte })
  //     if (gte !== undefined) {
  //       handleRoute({ action: 'SET', routes: { priceGte: gte }, push: false })
  //     } else {
  //       handleRoute({ action: 'DEL', routes: ['priceGte'], push: false })
  //     }
  //     if (lte !== undefined) {
  //       handleRoute({ action: 'SET', routes: { priceLte: lte }, push: false })
  //     } else {
  //       handleRoute({ action: 'DEL', routes: ['priceLte'], push: false })
  //     }
  //     handleRoute({})
  //   }
  // }

  const handleSwitch = (name: string, value: boolean): void => {
    setState({ [name]: value })
    if (name === 'hasSale') {
      if (value) {
        handleRoute({ action: 'SET', routes: { sale: 'true' } })
        trackShopEvent('shop_filter', {
          filter_type: 'sale',
          filter_value: 'true'
        })
      } else {
        handleRoute({ action: 'DEL', routes: ['sale'] })
      }
    }
    if (name === 'hasGift') {
      if (value) {
        handleRoute({ action: 'SET', routes: { gift: 'true' } })
        trackShopEvent('shop_filter', {
          filter_type: 'gift',
          filter_value: 'true'
        })
      } else {
        handleRoute({ action: 'DEL', routes: ['gift'] })
      }
    }
  }

  const handleRoute = (param: RouteParam): void => {
    if (param.routes !== undefined) {
      if (param.action === 'DEL' && Array.isArray(param.routes)) {
        for (const item of param.routes) {
          queryParams.delete(item)
        }
      } else {
        for (const [key, value] of Object.entries(param.routes)) {
          if (value !== undefined) {
            queryParams.set(key, value)
          }
        }
      }
    }
    if (param.push !== false) {
      const params = queryParams.toString()
      console.log('asdasd', queryParams)
      router.push(pathname + '?' + params)
    }
  }

  const fetchData = async (params: ShopParams): Promise<void> => {
    try {
      setState({ loading: true })
      const res = await getProducts(params)
      setState({ products: res.response.productList })
      setState({ loading: false })
    } catch (e) {
      setState({ loading: false })
      console.error(e)
    }
  }
  useEffect(() => {
    if ((homeContent?.onlineshopBanners) != null) {
      setTotalImages(homeContent.onlineshopBanners.length)
      trackPromoImpression(0)
    }
  }, [homeContent])

  const handleSlideChange = (swiper: SwiperType) => {
    const newIndex = swiper.realIndex
    if (newIndex !== currentIndex) {
      setCurrentIndex(newIndex)
      trackPromoImpression(newIndex)
    }
  }

  const handleSearchEdit = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ): void => {
    setState({ search: event.target.value })
  }
  const handleSearchSubmit = (): void => {
    setState({
      catId: '',
      subCatId: '',
      priceGte: '',
      priceLte: '',
      priceRangeIdx: -1,
      priceAsc: '',
      priceDesc: ''
    })
    handleClearFilters()
    if (state.search === '') {
      handleRoute({ action: 'DEL', routes: ['q'] })
    } else {
      handleRoute({ action: 'SET', routes: { q: state.search } })
      trackShopEvent('shop_search', { search_term: state.search })
    }
  }
  const handleApplyPriceFilter = (): void => {
    if (state.priceGte !== '') {
      handleRoute({
        action: 'SET',
        routes: { priceGte: state.priceGte },
        push: false
      })
    } else {
      handleRoute({ action: 'DEL', routes: ['priceGte'], push: false })
    }
    if (state.priceLte !== '') {
      handleRoute({
        action: 'SET',
        routes: { priceLte: state.priceLte },
        push: false
      })
    } else {
      handleRoute({ action: 'DEL', routes: ['priceLte'], push: false })
    }
    handleRoute({})
    const min = state.priceGte !== '' ? state.priceGte : '0'
    const max = state.priceLte !== '' ? state.priceLte : 'max'
    trackShopEvent('shop_filter', {
      filter_type: 'price',
      filter_value: `${min}-${max}`
    })
  }
  const handleSortChange = async (value: string): Promise<void> => {
    if (value === 'default') {
      handleClearFilters()
      setState({
        priceAsc: 'false',
        priceDesc: 'false'
      })
      handleRoute({
        action: 'DEL',
        routes: ['priceAsc', 'priceDesc'],
        push: true
      })
    } else if (value === 'priceDesc') {
      setState({ priceDesc: 'true' })
      handleRoute({
        action: 'SET',
        routes: { priceDesc: 'true', priceAsc: 'false' },
        push: true
      })
      trackShopEvent('shop_filter', {
        filter_type: 'sort',
        filter_value: 'priceDesc'
      })
    } else {
      setState({ priceAsc: 'true' })
      handleRoute({
        action: 'SET',
        routes: { priceAsc: 'true', priceDesc: 'false' },
        push: true
      })
      trackShopEvent('shop_filter', {
        filter_type: 'sort',
        filter_value: 'priceAsc'
      })
    }
    setShowSortOptions(false)
  }
  const handleClearFilters = (): void => {
    handleRoute({
      action: 'DEL',
      routes: [
        'catId',
        'subCatId',
        'priceGte',
        'priceLte',
        'hasGift',
        'hasSale',
        'priceAsc',
        'priceDesc'
      ],
      push: false
    })
  }
  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return
      }
      setState({ drawerOpen: open })
    }
  const filterSection = (): any => (
    <Box sx={{ width: 300 }} role='presentation'>
      <div className='bg-surfaceInverse h-screen'>
        <ListItem
          secondaryAction={
            <DarkIconButton
              type='text'
              icon={<CloseIcon />}
              onClick={() => setState({ drawerOpen: false })}
            />
          }
        >
          <ListItemText
            className='text-textHigh'
            primary={translate('shop.filter')}
          />
        </ListItem>
        <Divider className='text-secondaryMidStroke' />
        <div className='mt-4 '>
          {categoryTree !== undefined && categoryTree.length > 0
            ? (
              <div className='flex flex-col gap-4'>
                {categoryTree?.map((item: Category, i: number) => (
                  <div key={item.id}>
                    <ul
                      className={
                      'cursor-pointer p-2 rounded-3xl truncate ml-3 mr-3 ' +
                      (state.selected ===
                      (locale === 'en' ? item.nameEn : item.nameMn)
                        ? theme === 'dark'
                          ? 'bg-white'
                          : 'bg-[#222222]'
                        : theme === 'dark'
                          ? 'border bg-surfaceInverse rounded-2xl'
                          : 'border bg-surfaceInverse rounded-2xl')
                    }
                      onClick={() => {
            void handleCategorySelect(
              locale === 'en' ? item.nameEn : item.nameMn,
              item.id
            )
            setState({ selectedIndex: i })
          }}
                    >
                      <li
            className={classNames(
                        'lg:text-sm text-xs font-medium text-center',
                        state.selected ===
                          (locale === 'en' ? item.nameEn : item.nameMn)
                          ? theme === 'dark'
                            ? 'text-black'
                            : 'text-white'
                          : theme === 'dark'
                            ? 'text-textHigh'
                            : 'text-textHigh'
                      )}
          >
            {locale === 'en' ? item.nameEn : item.nameMn}
          </li>
                    </ul>
                    {state.selectedIndex === i &&
                    (state.selectedSubcategories != null) &&
                    state.selectedSubcategories.length > 0 && (
                      <div className='pl-4 pt-2 flex flex-col'>
                        {state.selectedSubcategories.map(
                          (subItem: Subcategory) => (
                            <FormControl className='pt-1 ml-2' key={subItem.id}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    color='error'
                                    checked={
                                      state.subCatId === subItem.id.toString()
                                    }
                                    onChange={async (e: any) =>
                                      await handleBrandSelect(subItem.id.toString())}
                                    className='-my-2 text-[#B0B0B0] rounded-xl'
                                    sx={{
                                      '& .MuiSvgIcon-root': {
                                        color:
                                          state.subCatId ===
                                          subItem.id.toString()
                                            ? '#EF4444'
                                            : '#B0B0B0'
                                      },
                                      '&, & + .MuiFormControlLabel-label': {
                                        color: '#262626',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        '@media (min-width: 1024px)': {
                                          fontSize: '14px'
                                        }
                                      }
                                    }}
                                  />
                                }
                                label={
                                  <span className='text-textHigh text-base'>
                                    {subItem.name}
                                  </span>
                                }
                              />
                            </FormControl>
                          )
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              )
            : null}
        </div>
        <Divider className='text-secondaryMidStroke mt-4' />
        {state.products !== undefined && state.products.length > 0
          ? (
            <div className='m-4'>
              <p className='text-lg font-medium text-textHigh w-1/2'>
                {translate('price')}
              </p>
              {state.openPrice === true
                ? (
                  <div className='mt-2'>
                    <div className='pr-2 pl-4'>
            <Slider
                      value={[
                        Number(
                          state.priceGte != null && state.priceGte !== ''
                            ? state.priceGte
                            : '0'
                        ),
                        Number(
                          state.priceLte != null && state.priceLte !== ''
                            ? state.priceLte
                            : '10000000'
                        )
                      ]}
                      onChange={(event, newValue) => {
                        const newValueArray = newValue as number[]
                        const [min, max] = newValueArray
                        setState({
                          priceGte: min.toString(),
                          priceLte: max.toString()
                        })
                      }}
                      onChangeCommitted={() => handleApplyPriceFilter()}
                      min={0}
                      max={10000000}
                      step={200}
                      valueLabelDisplay='auto'
                      valueLabelFormat={(value) => `${value.toLocaleString()}₮`}
                      aria-labelledby='range-slider'
                      sx={{
                        '& .MuiSlider-thumb': {
                          backgroundColor: 'white',
                          border: '2px solid #EF4444',
                          height: 24,
                          width: 24
                        },
                        '& .MuiSlider-rail': {
                          backgroundColor: '#ccc',
                          height: 8
                        },
                        '& .MuiSlider-track': {
                          backgroundColor: '#EF4444',
                          height: 10
                        }
                      }}
                      onTouchStart={(e) => e.stopPropagation()}
                    />
          </div>
                    <div className='flex justify-between gap-2'>
            <div className='flex-col justify-start items-start w-1/2'>
                      <p className='font-medium text-textHigh lg:text-sm text-xs'>
                        {translate('shop.minimum')}
                      </p>
                      <div className='flex self-stretch px-4 py-3 rounded-lg border-2 border-bordersm justify-center items-center mt-2'>
                        <span className='text-textMid'>
            {state.priceGte != null && state.priceGte !== ''
                          ? `${Number(state.priceGte).toLocaleString()}₮`
                          : '0₮'}
          </span>
                      </div>
                    </div>
            <div className='flex-col justify-start items-start w-1/2'>
                      <p className='font-medium text-textHigh lg:text-sm text-xs'>
                        {translate('shop.maximum')}
                      </p>
                      <div className='flex self-stretch px-4 py-3 rounded-lg border-2 border-bordersm justify-center items-center mt-2'>
                        <p className='text-textMid'>
            {state.priceLte != null && state.priceLte !== ''
                          ? `${Number(state.priceLte).toLocaleString()}₮`
                          : '10,000,000₮'}
          </p>
                      </div>
                    </div>
          </div>
                  </div>
                  )
                : null}
            </div>
            )
          : (
            <></>
            )}
        <Divider className='text-secondaryMidStroke mt-4' />
        <div
          className='flex justify-between w-full items-center cursor-pointer py-4'
          onClick={() => setState({ openType: !state.openType })}
        >
          <span className='text-lg font-medium text-textHigh w-1/2 ml-4'>
            {translate('shop.state')}
          </span>
          {state.openType
            ? (
              <KeyboardArrowUp className='text-textHigh mr-2' />
              )
            : (
              <KeyboardArrowDown className='text-textHigh mr-2' />
              )}
        </div>
        {state.openType && (
          <div className='m-4'>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    color='error'
                    checked={state.hasSale}
                    onChange={(e) => handleSwitch('hasSale', e.target.checked)}
                    className='-my-2 text-surfaceInverse rounded-xl'
                    sx={{
                      '& .MuiSvgIcon-root': {
                        color: state.hasSale ? '#EF4444' : '#B0B0B0',
                        borderRadius: '50%',
                        width: 20,
                        height: 20
                      },
                      '&, & + .MuiFormControlLabel-label': {
                        color: '#262626',
                        fontSize: '12px',
                        fontWeight: '500',
                        '@media (min-width: 1024px)': {
                          fontSize: '14px'
                        }
                      }
                    }}
                  />
                }
                label={
                  <span className='text-textHigh text-base'>
                    {translate('shop.hasSale')}
                  </span>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    color='error'
                    checked={state.hasGift}
                    onChange={(e) => handleSwitch('hasGift', e.target.checked)}
                    className='-my-2 text-[#B0B0B0] rounded-xl'
                    sx={{
                      '& .MuiSvgIcon-root': {
                        color: state.hasGift ? '#EF4444' : '#B0B0B0',
                        borderRadius: '50%',
                        width: 20,
                        height: 20
                      },
                      '&, & + .MuiFormControlLabel-label': {
                        color: '#262626',
                        fontSize: '12px',
                        fontWeight: '500',
                        '@media (min-width: 1024px)': {
                          fontSize: '14px'
                        }
                      }
                    }}
                  />
                }
                label={
                  <span className='text-textHigh text-base'>
                    {translate('shop.hasGift')}
                  </span>
                }
              />
            </FormGroup>
          </div>
        )}
      </div>
    </Box>
  )
  const searchBox = (): any => (
    <InputBase
      className=' bg-surfaceWebGrey border rounded-full pl-5 md:my-4 max-h-10 text-textHigh'
      value={state.search}
      sx={{ flex: 1 }}
      fullWidth
      placeholder={translate('search')}
      onChange={handleSearchEdit}
      endAdornment={
        <div className='flex flex-row'>
          <DarkIconButton
            type='text'
            iconImg={resolveImageUrl('/uploads/icons/search.svg')}
            onClick={handleSearchSubmit}
          />
          {/* <IconButton onClick={handleSearchSubmit}><Search /></IconButton> */}
        </div>
      }
      onKeyUp={(event) => {
        if (event.key === 'Enter') {
          handleSearchSubmit()
        }
      }}
    />
  )

  if (categoryTree !== undefined) {
    return (
      <div className='w-full flex justify-center items-center'>
        <div className='w-[95vw]'>
          <div className='w-full'>
            <div className='md:hidden mb-8'>{searchBox()}</div>
            <div className='w-full'>
              {((homeContent?.onlineshopBanners) != null) &&
              homeContent.onlineshopBanners.length > 0
                ? (
                  <div className='relative'>
                    <Swiper
                      modules={[Navigation, Autoplay]}
                      spaceBetween={5}
                      slidesPerView={1}
                      loop
                      autoplay={{ delay: 10000 }}
                      onSlideChange={handleSlideChange}
                      onSwiper={(swiper) => (swiperRef.current = swiper)}
                      className='w-full'
                    >
                      {homeContent?.onlineshopBanners.map((banner, index) => (
                        <SwiperSlide key={index}>
                          <div
                            className='w-full aspect-[6/1] md:aspect-[4/1] lg:aspect-[4/1] sm:aspect-[3/1] xs:aspect-[16/0] relative cursor-pointer'
                            onClick={() => { trackPromoClick(currentIndex) }}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                trackPromoClick(currentIndex)
                              }
                            }}
                            role='button'
                            tabIndex={0}
                          >
            <Image
                            src={resolveImageUrl(banner.image)}
                            alt={`Banner ${index}`}
                            className='rounded-xl w-full h-full object-cover'
                            layout='responsive'
                            width={1200}
                            height={200}
                            objectPosition='center'
                            unoptimized
                          />
          </div>
                        </SwiperSlide>
                      ))}
                    </Swiper>

                    {totalImages > 1 && (
                      <div className='absolute bottom-0 left-0 w-[180px] h-[28px] md:w-[210px] md:h-[32px] bg-black mb-2 ml-2 flex items-center justify-between px-2 rounded-xl z-20'>
                        <div className='w-full text-center text-white text-sm ml-[-4rem] sm:ml-[-4rem] md:ml-[-5rem]'>
                          <span>{`${currentIndex + 1}/${totalImages}`}</span>
                        </div>
                        <ol className='absolute left-20 sm:left-20 transform -translate-x-1/2 flex space-x-1'>
                          {Array.from({ length: totalImages }).map((_, index) => (
            <li
                            key={index}
                            className={`w-4 h-1.5 rounded-md cursor-pointer ${
                              currentIndex === index
                                ? 'bg-white'
                                : 'bg-gray-400'
                            }`}
                            onClick={() => {
                              setCurrentIndex(index)
                              swiperRef.current?.slideTo(index)
                            }}
                            style={{
                              transition: 'background-color 0.3s ease'
                            }}
                          />
          ))}
                        </ol>
                        <button
                          className='custom-swiper-prev text-white absolute top-1/2 right-7 sm:right-8 transform -translate-y-1/2 z-30'
                          onClick={() => swiperRef.current?.slidePrev()}
                        >
                          <ChevronLeft fontSize='small' />
                        </button>
                        <button
                          className='custom-swiper-next text-white absolute top-1/2 right-1 transform -translate-y-1/2 z-30'
                          onClick={() => swiperRef.current?.slideNext()}
                        >
                          <ChevronRight fontSize='small' />
                        </button>
                      </div>
                    )}
                  </div>
                  )
                : (homeContent != null)
                    ? null
                    : (
                      <div className='flex w-full h-screen justify-center items-center'>
                        <Loader />
                      </div>
                      )}
            </div>
            <div className='flex justify-between mt-4'>
              <h1 className='font-extrabold xl:text-3xl text-2xl mt-1 hidden md:flex text-textHigh'>
                {translate('shop')}
              </h1>
              <div className='bg-surfaceWebGrey p-3 rounded-xl inline-block border border-surfaceWebGray hidden md:flex flex-shrink-0'>
                <div className='flex justify-between items-center'>
                  <p className='text-xs text-textHigh text-center font-bold'>
                    {state.products?.length}
                  </p>
                  <p className='ml-1 text-xs text-textHigh text-center font-medium'>
                    {translate('sort.result')}
                  </p>
                  <div className='h-5 w-px bg-secondaryMidStroke mx-3' />
                  <div className='relative'>
                    <select
                      className='bg-surfaceWebGrey text-textHigh text-xs font-medium px-4 py-2 rounded-lg border border-surfaceWebGray appearance-none pr-10'
                      onChange={async (e) => await handleSortChange(e.target.value)}
                    >
                      <option value='default'>
                        {translate('sort.default')}
                      </option>
                      <option value='priceAsc'>
                        {translate('sort.priceAsc')}
                      </option>
                      <option value='priceDesc'>
                        {translate('sort.priceDesc')}
                      </option>
                    </select>
                    <div className='absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                        className='h-5 w-5 text-textHigh'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth='2'
                          d='M19 9l-7 7-7-7'
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              {/* <div className="relative md:hidden z-10">
                <DarkButton
                  type="outlined"
                  size="sm"
                  trailingIcon={<KeyboardArrowDown />}
                  onClick={() =>
                    setState({ showCategory: state.showCategory !== true })
                  }
                >
                  {state.selected}
                </DarkButton>
                {state.showCategory === true ? (
                  <ul className="p-4 rounded-2xl bg-surfaceInverse absolute top-12 w-fit border">
                    {categoryTree.length > 0
                      ? categoryTree.map((item: Category, i: number) => (
                          <li
                            key={i}
                            className="cursor-pointer truncate py-2 text-textHigh"
                            onClick={() => {
                              void handleCategorySelect(
                                locale === "en" ? item.nameEn : item.nameMn,
                                item.id
                              );
                            }}
                          >
                            {locale === "en" ? item.nameEn : item.nameMn}
                          </li>
                        ))
                      : null}
                  </ul>
                ) : (
                  // <FormControl
                  //   sx={{
                  //     mt: 3,
                  //     '& label.Mui-focused': {
                  //       color: '#262626'
                  //     },
                  //     '& fieldset': {
                  //       borderRadius: '12px',
                  //       borderColor: '#D6D6D6'
                  //     },
                  //     '& .MuiOutlinedInput-root': {
                  //       '&.Mui-focused fieldset': {
                  //         borderColor: '#D6D6D6'
                  //       }
                  //     }
                  //   }} className='w-full bg-[#F7F7F7]'
                  // >
                  //   { <InputLabel className='px-1'>Ухаалаг хэрэгсэл</InputLabel> }
                  //   <Select
                  //     name='selectedCityId'
                  //     className='rounded-lg'
                  //     MenuProps={MenuProps}
                  //   >
                  //     {categoryTree.length > 0
                  //       ? categoryTree.map((item: Category, i: number) =>
                  //         <MenuItem key={i} value={i} onClick={() => { void handleCategorySelect(item.nameMn) }}>{locale === 'en' ? item.nameEn : item.nameMn}</MenuItem>
                  //       // <li key={i} className='cursor-pointer bg-red-100' onClick={() => { void handleCategorySelect(item.nameMn) }}>{locale === 'en' ? item.nameEn : item.nameMn}</li>
                  //       )
                  //       : null}
                  //   </Select>
                  // </FormControl>
                  <></>
                )}
              </div> */}
              <div className='md:hidden z-10'>
                <DarkButton
                  type='outlined'
                  size='sm'
                  leadingIconImg={resolveImageUrl('/uploads/icons/sort.svg')}
                  trailingIcon={<KeyboardArrowDown />}
                  onClick={() => setState({ drawerOpen: true })}
                >
                  {translate('shop.filter')}
                </DarkButton>
              </div>
              <div className='md:hidden'>
                <div className='relative'>
                  <DarkButton
                    type='outlined'
                    size='sm'
                    leadingIconImg={resolveImageUrl('/uploads/icons/sort.svg')}
                    trailingIcon={<KeyboardArrowDown />}
                    onClick={() => {
                      setShowSortOptions(!showSortOptions)
                    }}
                  >
                    {translate('sort')}
                  </DarkButton>
                  {showSortOptions && (
                    <div className='absolute mt-2 w-full bg-surfaceInverse rounded-md shadow-lg z-10'>
                      <button
                        className='w-full text-left px-4 py-2 hover:bg-gray-800 text-sm text-textHigh'
                        onClick={() => {
                          handleSortChange('default')
                          setShowSortOptions(false)
                        }}
                      >
                        {translate('sort.default')}
                      </button>
                      <button
                        className='w-full text-left px-4 py-2 hover:bg-gray-800 text-sm text-textHigh'
                        onClick={() => {
                          handleSortChange('priceAsc')
                          setShowSortOptions(false)
                        }}
                      >
                        {translate('sort.priceAsc')}
                      </button>
                      <button
                        className='w-full text-left px-4 py-2 hover:bg-gray-800 text-sm text-textHigh'
                        onClick={() => {
                          handleSortChange('priceDesc')
                          setShowSortOptions(false)
                        }}
                      >
                        {translate('sort.priceDesc')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className='flex flex-row xl:mt-10 mt-6'>
            <div className='hidden md:flex flex-col bg-textInverse p-4 rounded-xl mr-8'>
              {categoryTree.length > 0
                ? (
                  <div className='flex flex-col gap-4'>
                    {categoryTree.map((item: Category, i: number) => (
                      <div key={item.id}>
                        <ul
                          className={
                          'cursor-pointer p-2 rounded-3xl truncate ' +
                          (state.selected ===
                          (locale === 'en' ? item.nameEn : item.nameMn)
                            ? theme === 'dark'
                              ? 'bg-white'
                              : 'bg-[#222222]'
                            : theme === 'dark'
                              ? 'border bg-surfaceInverse rounded-2xl'
                              : 'border bg-surfaceInverse rounded-2xl')
                        }
                          onClick={() => {
            void handleCategorySelect(
              locale === 'en' ? item.nameEn : item.nameMn,
              item.id
            )
            setState({ selectedIndex: i })
          }}
                        >
                          <li
            className={classNames(
                            'lg:text-sm text-xs font-medium text-center',
                            state.selected ===
                              (locale === 'en' ? item.nameEn : item.nameMn)
                              ? theme === 'dark'
                                ? 'text-black'
                                : 'text-white'
                              : theme === 'dark'
                                ? 'text-textHigh'
                                : 'text-textHigh'
                          )}
          >
            {locale === 'en' ? item.nameEn : item.nameMn}
          </li>
                        </ul>
                        {state.selectedIndex === i &&
                        (state.selectedSubcategories != null) &&
                        state.selectedSubcategories.length > 0 && (
                          <div className='pl-4 pt-2 flex flex-col'>
                            {state.selectedSubcategories.map(
                              (subItem: Subcategory) => (
                                <FormControl className='pt-2' key={subItem.id}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        color='error'
                                        checked={
                                          state.subCatId ===
                                          subItem.id.toString()
                                        }
                                        onChange={async (e: any) =>
                                          await handleBrandSelect(
                                            subItem.id.toString()
                                          )}
                                        className='-my-2 text-[#B0B0B0] rounded-xl border-radius:50%'
                                        sx={{
                                          '& .MuiSvgIcon-root': {
                                            borderRadius: '50%',
                                            color:
                                              state.subCatId ===
                                              subItem.id.toString()
                                                ? '#EF4444'
                                                : '#B0B0B0'
                                          },
                                          '&, & + .MuiFormControlLabel-label': {
                                            color: '#262626',
                                            fontSize: '12px',
                                            fontWeight: '500',
                                            '@media (min-width: 1024px)': {
                                              fontSize: '14px'
                                            }
                                          }
                                        }}
                                      />
                                    }
                                    label={
                                      <span className='text-textHigh'>
                                        {subItem.name}
                                      </span>
                                    }
                                  />
                                </FormControl>
                              )
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  )
                : null}

              {/* {categoryTree.length > 0 ? (
                <div className="flex flex-col gap-4">
                  {categoryTree.map((item: Category, i: number) => (
                    <ul
                      key={i}
                      className={
                        "cursor-pointer p-2 rounded-xl truncate hover:bg-[#222222] " +
                        (state.selected === item.nameMn ? "bg-[#222222]" : "")
                      }
                      onClick={() => {
                        void handleCategorySelect(
                          locale === "en" ? item.nameEn : item.nameMn,
                          item.id
                        );
                      }}
                    >
                      <li
                        className={classNames(
                          "lg:text-sm text-xs font-medium text-center",
                          state.selected === item.nameMn
                            ? "text-white"
                            : "text-textHigh"
                        )}
                      >
                        {locale === "en" ? item.nameEn : item.nameMn}
                      </li>
                    </ul>
                  ))}
                </div>
              ) : null} */}
              <Divider className='my-2 mt-5 xl:w-64 w-52 bg-secondaryMidStroke' />
              {/* {searchBox()}
              <Divider className='w-full bg-secondaryMainStroke' /> */}
              {state.hasSale === true ||
              state.hasGift === true ||
              state.subCatId !== '' ||
              (state.priceGte != null && state.priceGte !== '') ||
              (state.priceLte != null && state.priceLte !== '')
                ? (
                  <div>
                    <div className='flex gap-3 py-4'>
                      <span className='xl:text-sm text-xs text-textHigh font-semibold'>
                        {translate('shop.filter')}
                      </span>
                    </div>
                    <div className='flex flex-col'>
                      {state.hasSale === true
                        ? (
                          <div className='bg-[#2A2D341A] w-fit py-1 px-3 rounded-lg mt-3 flex flex-row gap-2 justify-center items-center cursor-pointer'>
            <p className='xl:text-sm text-xs font-semibold text-textHigh'>
                            {translate('shop.hasSale')}
                          </p>
            <CloseOutlined
                            className='xl:text-sm text-xs font-semibold text-textHigh'
                            onClick={() => {
                              void handleSwitch('hasSale', false)
                            }}
                          />
          </div>
                          )
                        : null}
                      {state.hasGift === true
                        ? (
                          <div className='bg-[#2A2D341A] w-fit py-1 px-3 rounded-lg mt-3 flex flex-row gap-2 justify-center items-center cursor-pointer'>
            <p className='xl:text-sm text-xs font-semibold text-textHigh'>
                            {translate('shop.hasGift')}
                          </p>
            <CloseOutlined
                            className='xl:text-sm text-xs font-semibold text-textHigh'
                            onClick={() => {
                              void handleSwitch('hasGift', false)
                            }}
                          />
          </div>
                          )
                        : null}
                      {state.subCatId !== '' && (state.selectedSubcategories != null)
                        ? (
                          <div className='bg-[#2A2D341A] w-fit py-1 px-3 rounded-lg mt-3 flex flex-row gap-2 justify-center items-center cursor-pointer'>
            <p className='xl:text-sm text-xs font-semibold text-textHigh'>
                            {state.selectedSubcategories.find(
                              (e) => e.id.toString() === state.subCatId
                            )?.name || ''}
                          </p>
            <CloseOutlined
                            className='xl:text-sm text-xs font-semibold text-textHigh'
                            onClick={() => {
                              void handleBrandSelect(state.subCatId)
                            }}
                          />
          </div>
                          )
                        : null}
                      {(state.priceGte != null && state.priceGte !== '') ||
                    (state.priceLte != null && state.priceLte !== '')
                        ? (
                          <div className='bg-[#2A2D341A] w-fit py-1 px-3 rounded-lg mt-3 flex flex-row gap-2 justify-center items-center cursor-pointer'>
            <p className='xl:text-sm text-xs font-semibold text-textHigh'>
                            {(state.priceGte != null && state.priceGte !== ''
                              ? Number(state.priceGte).toLocaleString()
                              : '0') + '₮'}{' '}
                            -
                            {(state.priceLte != null && state.priceLte !== ''
                              ? Number(state.priceLte).toLocaleString()
                              : '∞') + '₮'}
                          </p>
            <CloseOutlined
                            className='xl:text-sm text-xs font-semibold text-textHigh'
                            onClick={() => {
                              setState({ priceGte: '', priceLte: '' })
                              handleRoute({
                                action: 'DEL',
                                routes: ['priceGte', 'priceLte']
                              })
                            }}
                          />
          </div>
                          )
                        : null}
                    </div>
                    <Divider className='w-full m-2 bg-secondaryMidStroke' />
                  </div>
                  )
                : (
                  <></>
                  )}
              <div className='w-full'>
                <div>
                  <div
                    className='flex justify-between w-full items-center cursor-pointer py-4'
                    onClick={() =>
                      setState({ openType: state.openType !== true })}
                  >
                    <span className='font-bold text-textHigh lg:text-base text-sm'>
                      {translate('shop.state')}
                    </span>
                    {state.openType === true
                      ? (
                        <KeyboardArrowUp className='text-textHigh' />
                        )
                      : (
                        <KeyboardArrowDown className='text-textHigh' />
                        )}
                  </div>
                  {state.openType === true
                    ? (
                      <div className='flex flex-col'>
                        <FormControl className='pt-2'>
                          <FormControlLabel
                            control={
                              <Checkbox
            color='error'
            size='small'
            checked={state.hasSale}
            onChange={(e: any) =>
                                handleSwitch('hasSale', e.target.checked)}
            className='-my-2 text-[#B0B0B0] rounded-xl'
            sx={{
                                '& .MuiSvgIcon-root': {
                                  color: state.hasSale ? '#EF4444' : '#B0B0B0'
                                },
                                '&, & + .MuiFormControlLabel-label': {
                                  color: '#262626',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  '@media (min-width: 1024px)': {
                                    fontSize: '14px'
                                  }
                                }
                              }}
          />
                          }
                            label={
                              <span className='text-textHigh'>
            {translate('shop.hasSale')}
          </span>
                          }
                          />
                        </FormControl>
                        <FormControl className='pt-2'>
                          <RadioGroup value={state.subCatId}>
                            <FormControlLabel
                              control={
            <Checkbox
                                color='error'
                                size='small'
                                checked={state.hasGift}
                                onChange={(e: any) =>
                                  handleSwitch('hasGift', e.target.checked)}
                                className='-my-2 text-[#B0B0B0] rounded-xl'
                                sx={{
                                  '& .MuiSvgIcon-root': {
                                    color: state.hasGift
                                      ? '#EF4444'
                                      : '#B0B0B0'
                                  },
                                  '&, & + .MuiFormControlLabel-label': {
                                    color: '#262626',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    '@media (min-width: 1024px)': {
                                      fontSize: '14px'
                                    }
                                  }
                                }}
                              />
                            }
                              label={
            <span className='text-textHigh'>
                                {translate('shop.hasGift')}
                              </span>
                            }
                            />
                          </RadioGroup>
                        </FormControl>
                      </div>
                      )
                    : (
                      <></>
                      )}
                  <Divider className='mt-2 w-full bg-secondaryMidStroke' />
                </div>

                {state.products !== undefined && state.products.length > 0
                  ? (
                    <div>
                      <div
                        className='flex justify-between w-full items-center cursor-pointer py-4'
                        onClick={() =>
                          setState({ openPrice: state.openPrice !== true })}
                      >
                        <span className='font-bold text-textHigh lg:text-base text-sm'>
                          {translate('price')}
                        </span>
                        {state.openPrice === true
                          ? (
                            <KeyboardArrowUp className='text-textHigh' />
                            )
                          : (
                            <KeyboardArrowDown className='text-textHigh' />
                            )}
                      </div>
                      {state.openPrice === true
                        ? (
                          <div className='mt-1'>
                            <div className='px-3'>
            <Slider
                              value={[
                                Number(
                                  state.priceGte != null && state.priceGte !== ''
                                    ? state.priceGte
                                    : '0'
                                ),
                                Number(
                                  state.priceLte != null && state.priceLte !== ''
                                    ? state.priceLte
                                    : '10000000'
                                )
                              ]}
                              onChange={(event, newValue) => {
                                const newValueArray = newValue as number[]
                                const [min, max] = newValueArray
                                setState({
                                  priceGte: min.toString(),
                                  priceLte: max.toString()
                                })
                              }}
                              onChangeCommitted={handleApplyPriceFilter}
                              min={0}
                              max={10000000}
                              step={200}
                              valueLabelDisplay='auto'
                              valueLabelFormat={(value) =>
                              `${value.toLocaleString()}₮`}
                              aria-labelledby='range-slider'
                              sx={{
                                '& .MuiSlider-thumb': {
                                  backgroundColor: 'white',
                                  border: '2px solid #EF4444',
                                  height: 24,
                                  width: 24
                                },
                                '& .MuiSlider-rail': {
                                  backgroundColor: '#ccc',
                                  height: 8
                                },
                                '& .MuiSlider-track': {
                                  backgroundColor: '#EF4444',
                                  height: 10
                                }
                              }}
                            />
          </div>
                            <div className='flex justify-between gap-2'>
            <div className='flex-col justify-start items-start w-1/2'>
                              <p className='font-medium text-textMid lg:text-sm text-xs'>
                                {translate('shop.minimum')}
                              </p>
                              <div className='flex self-stretch px-4 py-3 rounded-lg border-2 border-bordersm justify-center items-center mt-2'>
                                <span className='text-textMid'>
            {state.priceGte != null && state.priceGte !== ''
                                  ? `${Number(
                                      state.priceGte
                                    ).toLocaleString()}₮`
                                  : '0₮'}
          </span>
                              </div>
                            </div>
            <div className='flex-col justify-start items-start w-1/2'>
                              <p className='font-medium text-textMid lg:text-sm text-xs'>
                                {translate('shop.maximum')}
                              </p>
                              <div className='flex self-stretch px-4 py-3 rounded-lg border-2 border-bordersm justify-center items-center mt-2'>
                                <p className='text-textMid'>
            {state.priceLte != null && state.priceLte !== ''
                                  ? `${Number(
                                      state.priceLte
                                    ).toLocaleString()}₮`
                                  : '10,000,000₮'}
          </p>
                              </div>
                            </div>
          </div>
                          </div>
                          )
                        : null}
                    </div>
                    )
                  : (
                    <></>
                    )}
              </div>
              {/* {searchBox()} */}
            </div>
            {/* <Divider
              orientation="vertical"
              className="mx-10 hidden md:flex bg-secondaryMidStroke"
              flexItem
            /> */}
            {/* ---- Webview ---- */}
            <div className='w-full'>
              {/* <div className='w-full px-4 mt-2 md:hidden'>
                <div id='mobile-top' className='flex flex-row justify-between'>
                  <Select
                    size='small'
                    onChange={(e: SelectChangeEvent) => { void handleCategorySelect(e.target.value) }}
                    label='Brand'
                    className='text-[#ED1C24] font-medium shadow-none'
                    value={state.selected}
                  >
                    <MenuItem key={-1} value=translate('all')>{translate('all')}</MenuItem>
                    {categoryTree.length > 0
                      ? categoryTree.map((item: Category, i: number) =>
                        <MenuItem key={i} value={item.nameMn}>{item.nameMn}</MenuItem>
                      )
                      : null}
                  </Select>
                  <Button variant='outlined' className='border-gray-200 normal-case font-semibold' onClick={() => setState({ drawerOpen: true })}>
                    <div>
                      <FilterListIcon className='mr-2' />
                      {translate('shop.filter')}
                    </div>
                  </Button>
                </div>
              </div> */}
              {state.loading === true
                ? (
                  <div className='w-full grid grid-cols-2 md:grid-cols-3 gap-4 mt-5'>
                    {[1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1].map(
                      (item: number, index: number) => (
                        <div className='' key={index}>
                          <Skeleton variant='rounded' height={230} width={170} />
                        </div>
                      )
                    )}
                  </div>
                  )
                : state.products !== undefined && state.products.length > 0
                  ? (
                    <div className='grid grid-cols-2 xl:grid-cols-4 lg:grid-cols-3 lg:gap-5 md:gap-3 gap-2'>
                      {state.products.map((item: ProductMin, i: number) => (
                        <ProductCard
                          key={item.id}
                          product={item}
                          brand={
                        subcategories?.find((e) => e.id === item.subCategoryId)
                          ?.name ?? ''
                      }
                        />
                      ))}
                    </div>
                    )
                  : (
                    <div className='flex flex-col items-center justify-center p-10'>
                      <Image
                        draggable={false}
                        width={200}
                        height={200}
                        className='overflow-hidden object-cover mt-16'
                        src='/images/noProductFound.png'
                        alt='No item'
                      />
                      <p className='mt-5 text-textHigh'>{translate('noResult')}</p>
                    </div>
                    )}
            </div>
          </div>
        </div>
        <>
          <SwipeableDrawer
            anchor='left'
            open={state.drawerOpen}
            onClose={toggleDrawer(false)}
            onOpen={toggleDrawer(true)}
          >
            {filterSection()}
          </SwipeableDrawer>
        </>
      </div>
    )
  } else {
    return (
      <div className='flex w-full h-screen justify-center items-center'>
        <Loader />
      </div>
    )
  }
}
export default Shop
